/**
 * HistoryCard Component
 *
 * Form card for viewing and editing valuation history entries.
 * Manages its own state for add/edit operations.
 *
 * Props:
 *   - history: Array of { date, valuation } records
 *   - historyLoading: Boolean indicating if history is being fetched
 *   - historyValidationError: Error string from validation hook
 *   - validateHistoryEntry: Function(date, value, history, excludeIndex) from validation hook
 *   - clearHistoryError: Function to clear validation error
 *   - onHistoryChange: Function(newHistory) callback when history is modified
 *   - isAsset: Boolean
 *   - isLiability: Boolean
 */

import { useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { XIcon, TrashSimpleIcon, PlusIcon, CheckIcon } from '@phosphor-icons/react';
import { CURRENCY_LONG, LOCALE } from 'utilities/global.variables';
import { formatCurrency, formatDate } from 'utilities/global.functions';

const formatValue = (value) => {
    return formatCurrency(value);
};

const HistoryCard = ({ history, historyLoading, historyValidationError, validateHistoryEntry, clearHistoryError, onHistoryChange, isAsset, isLiability }) => {

    // ── State ─────────────────────────────────────────────────────────────────

    const [addingEntry,   setAddingEntry]   = useState(false);
    const [newDate,       setNewDate]       = useState('');
    const [newValue,      setNewValue]      = useState('');

    const [editingIndex,  setEditingIndex]  = useState(null);
    const [editDate,      setEditDate]      = useState('');
    const [editValue,     setEditValue]     = useState('');

    // ── Type configuration ────────────────────────────────────────────────────

    const inputPrefix      = '£';
    const inputPlaceholder = '£';

    const displayValuation = (rawValuation) => formatValue(Math.abs(rawValuation));

    const toStoredValuation = (inputValue) => parseFloat(inputValue);

    // ── Add entry ─────────────────────────────────────────────────────────────

    const startAdd = () => {
        if (editingIndex !== null) cancelEdit();
        clearHistoryError();
        setAddingEntry(true);
    };

    const cancelAdd = () => {
        setAddingEntry(false);
        setNewDate('');
        setNewValue('');
        clearHistoryError();
    };

    const confirmAdd = () => {
        if (!validateHistoryEntry(newDate, newValue, history)) return;

        const updated = [...(history || []), { date: newDate, valuation: toStoredValuation(newValue) }];
        updated.sort((a, b) => new Date(b.date) - new Date(a.date));

        onHistoryChange(updated);
        cancelAdd();
    };

    // ── Edit entry ────────────────────────────────────────────────────────────

    const startEdit = (index) => {
        if (addingEntry) cancelAdd();
        const entry = history[index];
        setEditingIndex(index);
        setEditDate(entry.date);
        setEditValue(Math.abs(entry.valuation).toString()); // load magnitude, sign is a display concern
        clearHistoryError();
    };

    const cancelEdit = () => {
        setEditingIndex(null);
        setEditDate('');
        setEditValue('');
        clearHistoryError();
    };

    const confirmEdit = () => {
        if (!validateHistoryEntry(editDate, editValue, history, editingIndex)) return;

        const updated = [...(history || [])];
        updated[editingIndex] = { date: editDate, valuation: toStoredValuation(editValue) };
        updated.sort((a, b) => new Date(b.date) - new Date(a.date));

        onHistoryChange(updated);
        cancelEdit();
    };

    const confirmDelete = () => {
        const updated = [...(history || [])];
        updated.splice(editingIndex, 1);

        onHistoryChange(updated);
        cancelEdit();
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const today = new Date().toISOString().split('T')[0];

    return (
        <div className="History">
            <div className="card">

                <div className="flex row medium-gap center space">
                    <p className="regular">History</p>
                    {historyValidationError}
                </div>

                <div className="form-group flex col margin-top-large medium-gap">

                    {historyLoading && <p className="small">Loading history...</p>}

                    {!historyLoading && history?.length > 0 && (
                        <div className="flex col medium-gap">
                            {history.map((entry, index) =>
                                editingIndex === index ? (
                                    <div key={index} className="form-item editing">
                                        <div className="form-date">
                                            <label>
                                                <input
                                                    type="date"
                                                    max={today}
                                                    value={editDate}
                                                    onChange={(e) => setEditDate(e.target.value)}
                                                />
                                            </label>
                                        </div>
                                        <CurrencyInput
                                            intlConfig={{ locale: LOCALE, currency: CURRENCY_LONG.currency }}
                                            className="number medium"
                                            placeholder={inputPlaceholder}
                                            prefix={inputPrefix}
                                            allowNegativeValue={false}
                                            allowDecimals={false}
                                            value={editValue || 0}
                                            onValueChange={(value) => setEditValue(value)}
                                        />
                                        <div className="flex row medium-gap end">
                                            <button type="button" className="button regular" onClick={confirmDelete}><TrashSimpleIcon /></button>
                                            <button type="button" className="button regular" onClick={cancelEdit}><XIcon /></button>
                                            <button type="button" className="button regular" onClick={confirmEdit}><CheckIcon /></button>
                                        </div>
                                    </div>
                                ) : (
                                    <div key={index} className="form-item history-row" onClick={() => startEdit(index)}>
                                        <div className="form-date"><label>{formatDate(entry.date)}</label></div>
                                        <div className="form-valuation"><p className="number medium">{displayValuation(entry.valuation)}</p></div>
                                    </div>
                                )
                            )}
                        </div>
                    )}

                    <div className="flex col">
                        {!addingEntry ? (
                            <button type="button" className="button medium" onClick={startAdd}>
                                <PlusIcon size={20} />
                            </button>
                        ) : (
                            <div className="form-item editing">
                                <div className="form-date">
                                    <label>
                                        <input
                                            type="date"
                                            max={today}
                                            value={newDate}
                                            onChange={(e) => setNewDate(e.target.value)}
                                        />
                                    </label>
                                </div>
                                <CurrencyInput
                                    id="history_new"
                                    intlConfig={{ locale: LOCALE, currency: CURRENCY_LONG.currency }}
                                    className="number medium"
                                    placeholder={inputPlaceholder}
                                    prefix={inputPrefix}
                                    allowNegativeValue={false}
                                    allowDecimals={false}
                                    value={newValue || 0}
                                    onValueChange={(value) => setNewValue(value)}
                                />
                                <div className="flex row medium-gap end">
                                    <button type="button" className="button regular" onClick={cancelAdd}><XIcon /></button>
                                    <button type="button" className="button regular" onClick={confirmAdd}><CheckIcon /></button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default HistoryCard;
